import { useEffect, useState, useRef } from "react";
import {
  FaBolt,
  FaBuilding,
  FaChartLine,
  FaCheckCircle,
  FaGlobeAfrica,
  FaHardHat,
  FaShieldAlt,
  FaUsers,
} from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";

export default function HomeStats() {
  const [counts, setCounts] = useState({ years: 0, projects: 0, clients: 0, certified: 0 });
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    AOS.init({ duration: 700, once: true, offset: 100, easing: "ease-in-out" });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          animateCount("years", 6, 1500);
          animateCount("projects", 12, 1800);
          animateCount("clients", 25, 2000);
          animateCount("certified", 7, 1200);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  const animateCount = (key, target, duration) => {
    let start = 0;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCounts((prev) => ({ ...prev, [key]: target }));
        clearInterval(timer);
      } else {
        setCounts((prev) => ({ ...prev, [key]: Math.floor(start) }));
      }
    }, 16);
  };

  const stats = [
    {
      icon: <FaChartLine className="fs-2" />,
      number: `${counts.years}+`,
      suffix: "Years",
      label: "Proven expertise in electrical & civil engineering across Tanzania",
      color: "from-blue-500 to-cyan-400",
      bgColor: "bg-blue-50",
      iconColor: "text-blue-600",
    },
    {
      icon: <FaBolt className="fs-2" />,
      number: `${counts.projects}+`,
      suffix: "Projects",
      label: "Successfully delivered HV/LV installations, transformers & infrastructure",
      color: "from-amber-500 to-orange-400",
      bgColor: "bg-amber-50",
      iconColor: "text-amber-600",
    },
    {
      icon: <FaGlobeAfrica className="fs-2" />,
      number: `${counts.clients}+`,
      suffix: "Clients",
      label: "Trusted by Songas, REA, DIT, universities & private sector leaders",
      color: "from-emerald-500 to-teal-400",
      bgColor: "bg-emerald-50",
      iconColor: "text-emerald-600",
    },
    {
      icon: <FaShieldAlt className="fs-2" />,
      number: `${counts.certified}`,
      suffix: "Certifications",
      label: "CRB Electrical & Civil, TANESCO Licensed, VAT/TIN Registered",
      color: "from-violet-500 to-purple-400",
      bgColor: "bg-violet-50",
      iconColor: "text-violet-600",
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="MainBodySection position-relative overflow-hidden py-5"
      style={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)",
      }}
    >
      {/* Decorative Background Elements */}
      <div className="position-absolute top-0 start-0 w-100 h-100 opacity-10">
        <div className="position-absolute top-10 start-10" style={{ width: "200px", height: "200px", border: "1px solid #3B82F6", borderRadius: "50%" }} />
        <div className="position-absolute bottom-20 end-20" style={{ width: "150px", height: "150px", border: "1px solid #10B981", borderRadius: "50%" }} />
        <div className="position-absolute top-1/2 start-1/2 translate-middle" style={{ width: "300px", height: "300px", border: "1px solid #8B5CF6", borderRadius: "50%", opacity: 0.3 }} />
      </div>

      <div className="container px-3 position-relative">
        <div className="row g-4 align-items-center">
          {/* Left Content */}
          <div className="col-lg-4" data-aos="fade-right">
            <div className="text-white">
              <h2 className="display-6 font-bold mb-4">
                Engineering Impact, <span className="text-primary">Measured in Results</span>
              </h2>
              <p className="lead text-light mb-4" style={{ lineHeight: 1.7 }}>
                From high-voltage power lines to renewable energy systems, we deliver infrastructure that powers Tanzania's growth—on time, on spec, on budget.
              </p>
              <div className="d-flex flex-wrap gap-2">
                {["CRB Certified", "TANESCO Licensed", "ISO Compliant"].map((badge, i) => (
                  <span key={i} className="badge bg-white bg-opacity-10 text-white border border-white border-opacity-20 px-3 py-2 rounded-pill">
                    <FaCheckCircle className="text-success me-1" /> {badge}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="col-lg-8">
            <div className="row g-4">
              {stats.map((stat, index) => (
                <div
                  key={stat.label}
                  className="col-sm-6"
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <div
                    className="card border-0 h-100 position-relative overflow-hidden"
                    style={{
                      borderRadius: "20px",
                      background: "rgba(255, 255, 255, 0.08)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.15)",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-5px)";
                      e.currentTarget.style.boxShadow = "0 20px 40px rgba(0, 0, 0, 0.3)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    {/* Gradient Accent Bar */}
                    <div className={`position-absolute top-0 start-0 w-100`} style={{ height: "4px", background: `linear-gradient(90deg, ${stat.color})` }} />

                    <div className="card-body p-4">
                      {/* Icon */}
                      <div className={`d-inline-flex p-3 rounded-4 mb-3 ${stat.bgColor} bg-opacity-20`}>
                        <div className={stat.iconColor}>{stat.icon}</div>
                      </div>

                      {/* Animated Number */}
                      <div className="mb-2">
                        <span className="display-5 fw-bold text-white d-flex gap-2 align-items-center">
                          <span>{stat.number}</span>
                          <span className="fs-4 text-white-50 fw-normal">{stat.suffix}</span>
                        </span>
                      </div>

                      {/* Description */}
                      <p className="text-white-50 mb-0 small" style={{ lineHeight: 1.6 }}>
                        {stat.label}
                      </p>
                    </div>

                    {/* Subtle Hover Glow */}
                    <div
                      className="position-absolute bottom-0 start-0 w-100 opacity-0"
                      style={{
                        height: "3px",
                        background: `linear-gradient(90deg, ${stat.color})`,
                        transition: "opacity 0.3s ease",
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Trust Bar */}
        <div className="row mt-5 pt-4" data-aos="fade-up" data-aos-delay="400">
          <div className="col-12">
            <div className="d-flex flex-wrap justify-content-center align-items-center gap-4 py-3" style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}>
              <div className="d-flex align-items-center text-white-50">
                <FaUsers className="me-2 text-primary" />
                <span className="small">Expert Team: 7+ Certified Engineers</span>
              </div>
              <div className="d-flex align-items-center text-white-50">
                <FaBuilding className="me-2 text-primary" />
                <span className="small">Serving: Dar es Salaam & Nationwide</span>
              </div>
              <div className="d-flex align-items-center text-white-50">
                <FaShieldAlt className="me-2 text-primary" />
                <span className="small">100% Compliance</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}